import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

interface TableTopBarProps {
  serch: boolean;
  serchOpen: boolean;
  setSerchOpen: (serchOpen: boolean) => void;
  addDataNames: string[];
  addDataName: string;
  setAddDataName: (name: string) => void;
  addCSVDataNames: string[];
  addCSVDataName: string;
  setAddCSVDataName: (name: string) => void;
  getXLSXDataNames: string[];
  getXLSXDataName: string;
  setGetXLSXDataName: (name: string) => void;
  editDatasNames: string[];
  editDatasName: string;
  setEditDatasName: (name: string) => void;
  editIDs: number[];
  columnSettingOpen: boolean;
  openColumnSettingWindow: () => void;
  // 一括設定ドロップダウン
  bulkSettingNames?: string[];
  bulkSettingName?: string;
  setBulkSettingName?: (name: string) => void;
  // AIチャット
  aiChatOpen?: boolean;
  setAiChatOpen?: (open: boolean) => void;
  // E2Eテスト用 data-testid
  addDataTestIds?: string[];
  bulkSettingTestIds?: string[];
  // ツールバー拡張
  totalCount?: number;
  onReload?: () => void;
  splitHeaderButtons?: boolean;
}

function TableTopBar(props: TableTopBarProps) {
  const {
    serch,
    serchOpen,
    setSerchOpen,
    addDataNames,
    addDataName,
    setAddDataName,
    addCSVDataNames,
    addCSVDataName,
    setAddCSVDataName,
    getXLSXDataNames,
    getXLSXDataName,
    setGetXLSXDataName,
    editDatasNames,
    editDatasName,
    setEditDatasName,
    editIDs,
    columnSettingOpen,
    openColumnSettingWindow,
    bulkSettingNames = [],
    bulkSettingName,
    setBulkSettingName,
    aiChatOpen,
    setAiChatOpen,
    addDataTestIds = [],
    bulkSettingTestIds = [],
    totalCount,
    onReload,
    splitHeaderButtons = false,
  } = props;

  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const pathname = useAtomValue(AppAtoms.pathname);
  // cmd-po_1243(R40): SP時、上段のタブ帯(PageTabs)がfixedで居るぶんだけ自身のfixed topを下げてタブの裏に回らないようにする。タブ無し画面は0=従来どおり56px
  const spPageTabsHeight = useAtomValue(AppAtoms.spPageTabsHeight);
  // cmd-po_1168 rw1: atom一本化（FABトグルはAppShell直トグル・ブリッジ退役）
  const setAiChatOpenAtom = useSetAtom(AppAtoms.aiChatOpen);
  const setHeaderCreateAction = useSetAtom(AppAtoms.headerCreateAction);
  const setHeaderCreateActions = useSetAtom(AppAtoms.headerCreateActions);

  // 複数 addDataNames の場合: Menu アンカー（ヘッダーボタンクリック位置固定）
  const addMenuAnchorRef = useRef<HTMLSpanElement | null>(null);
  // cmd-po_1230 rw1: SP時のツールバー帯fixed化に伴う content-jump 防止用の実高計測
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [anchorElMenuAddData, setAnchorElMenuAddData] =
    React.useState<null | HTMLElement>(null);

  const [anchorElMenuEditDatas, setAnchorElMenuEditDatas] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuEditDatas = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuEditDatas(event.currentTarget);
  };
  const handleCloseMenuEditDatas = () => {
    setAnchorElMenuEditDatas(null);
  };

  const [anchorElMenuAddCSVData, setAnchorElMenuAddCSVData] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuAddCSVData = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuAddCSVData(event.currentTarget);
  };
  const handleCloseMenuAddCSVData = () => {
    setAnchorElMenuAddCSVData(null);
  };

  const [anchorElMenuGetXLSXData, setAnchorElMenuGetXLSXData] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuGetXLSXData = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuGetXLSXData(event.currentTarget);
  };
  const handleCloseMenuGetXLSXData = () => {
    setAnchorElMenuGetXLSXData(null);
  };

  const [anchorElMenuBulkSetting, setAnchorElMenuBulkSetting] =
    React.useState<null | HTMLElement>(null);
  const handleOpenMenuBulkSetting = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenuBulkSetting(event.currentTarget);
  };
  const handleCloseMenuBulkSetting = () => {
    setAnchorElMenuBulkSetting(null);
  };

  // ─── headerCreateAction atom 登録 ─────────────────────────────────
  useEffect(() => {
    if (addDataNames.length === 0) {
      setHeaderCreateAction(null);
      setHeaderCreateActions(null);
      return () => { setHeaderCreateAction(null); setHeaderCreateActions(null); };
    }
    if (splitHeaderButtons && addDataNames.length > 1) {
      setHeaderCreateAction(null);
      setHeaderCreateActions(addDataNames.map((name, i) => ({
        label: name,
        handler: () => setAddDataName(name),
        testId: addDataTestIds[i],
      })));
      return () => { setHeaderCreateAction(null); setHeaderCreateActions(null); };
    }
    if (addDataNames.length === 1) {
      setHeaderCreateActions(null);
      setHeaderCreateAction({
        label: addDataNames[0],
        handler: () => setAddDataName(addDataNames[0]),
        testId: addDataTestIds[0],
      });
    } else {
      setHeaderCreateActions(null);
      setHeaderCreateAction({
        label: "作成",
        handler: () => {
          if (addMenuAnchorRef.current) setAnchorElMenuAddData(addMenuAnchorRef.current);
        },
      });
    }
    return () => { setHeaderCreateAction(null); setHeaderCreateActions(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDataNames.length, splitHeaderButtons]);

  // cmd-po_1249（殿裁定 2026-08-27）: FAB表示要求useEffectを退役（AppShellが無条件描画に変更・FAB表示シグナルatomを廃止）

  // cmd-po_1230 rw1: SP時のみ fixed化したツールバーの実高を計測して同高のspacerに反映（flexWrapで段が増えても追従）。PCでは0=spacer非描画
  useEffect(() => {
    if (!isSmartPhone) {
      setToolbarHeight(0);
      return;
    }
    const el = toolbarRef.current;
    if (!el) return;
    const measure = () => setToolbarHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isSmartPhone]);

  const tbBtnSx = {
    fontSize: "13px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    height: "var(--control-h, 36px)",
    textTransform: "none",
    borderColor: "var(--color-border, #e5e5e0)",
    color: "var(--color-ai, #1e3a5f)",
    bgcolor: "background.paper",
    "&:hover": { borderColor: "var(--color-ai-light, #2d4a6f)", bgcolor: "var(--state-hover, rgba(30,58,95,0.05))" },
  } as const;

  return (
    <>
    <Box
      ref={toolbarRef}
      data-testid="table-toolbar"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-2, 8px)",
        flexWrap: "wrap",
        marginBottom: "var(--sp-1, 4px)",
        flexShrink: 0,
        // cmd-po_1230 rw1: SP限定でツールバー帯をアプリヘッダー直下にfixed固定（sticky はAppShell内側wrapperのoverflow:hiddenで効かないため fixed へ変更）。左右0+px で帯化。PCは全プロパティundefined=不変
        position: isSmartPhone ? "fixed" : undefined,
        top: isSmartPhone ? `calc(var(--header-h, 56px) + ${spPageTabsHeight}px)` : undefined,
        left: isSmartPhone ? 0 : undefined,
        right: isSmartPhone ? 0 : undefined,
        zIndex: isSmartPhone ? 100 : undefined,
        bgcolor: isSmartPhone ? "background.paper" : undefined,
        paddingTop: isSmartPhone ? "var(--sp-1, 4px)" : undefined,
        paddingBottom: isSmartPhone ? "var(--sp-1, 4px)" : undefined,
        paddingLeft: isSmartPhone ? "var(--sp-3, 12px)" : undefined,
        paddingRight: isSmartPhone ? "var(--sp-3, 12px)" : undefined,
      }}
    >
      {/* 複数addDataNames: Menu アンカー（不可視・ヘッダーボタン押下位置）*/}
      {addDataNames.length > 1 && (
        <span ref={addMenuAnchorRef} style={{ position: "fixed", top: 56, right: 16, width: 0, height: 0 }} />
      )}

      {/* 複数 addDataNames の Menu */}
      <Menu
        anchorEl={anchorElMenuAddData}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorElMenuAddData)}
        onClose={() => setAnchorElMenuAddData(null)}
      >
        {addDataNames.map((dataName, i) => (
          <MenuItem
            key={dataName}
            data-testid={addDataTestIds[i]}
            onClick={() => {
              setAddDataName(dataName);
              setAnchorElMenuAddData(null);
            }}
          >
            <Box className="header-manu">{dataName}</Box>
          </MenuItem>
        ))}
      </Menu>

      {/* 左: 全N件 + リロード */}
      {totalCount !== undefined && (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {`全${totalCount}件`}
        </Typography>
      )}
      {onReload && (
        <Tooltip title="リロード">
          <IconButton
            size="small"
            onClick={onReload}
            sx={{ width: 28, height: 28 }}
            aria-label="リロード"
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* spacer */}
      <Box sx={{ flex: 1 }} />

      {/* editDatasNames (一括編集) */}
      {editDatasNames.length > 0 ? (
        <>
          <Tooltip title={editDatasNames.length === 1 ? editDatasNames[0] : "新規登録"}>
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={editIDs.length === 0}
                startIcon={<EditNoteIcon sx={{ fontSize: "16px !important" }} />}
                onClick={(e) => {
                  if (editDatasNames.length === 1) {
                    setEditDatasName(editDatasNames[0]);
                  } else {
                    handleOpenMenuEditDatas(e);
                  }
                }}
                sx={{ ...tbBtnSx, color: editIDs.length === 0 ? undefined : editDatasName !== "" ? "primary.dark" : "var(--color-ai, #1e3a5f)" }}
              >
                {editDatasNames.length === 1 ? editDatasNames[0] : "新規登録"}
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={anchorElMenuEditDatas}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElMenuEditDatas)}
            onClose={handleCloseMenuEditDatas}
          >
            {editDatasNames.map((dataName) => (
              <MenuItem
                key={dataName}
                onClick={() => {
                  setEditDatasName(dataName);
                  handleCloseMenuEditDatas();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {/* bulkSettingNames (一括設定) Button+Menu（CSV取込同型・件20） */}
      {bulkSettingNames.length > 0 && !isSmartPhone ? (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryAddCheckIcon sx={{ fontSize: "16px !important" }} />}
            data-testid="bulk-setting-button"
            onClick={(e) => {
              if (bulkSettingNames.length === 1) {
                if (setBulkSettingName) setBulkSettingName(bulkSettingNames[0]);
              } else {
                handleOpenMenuBulkSetting(e);
              }
            }}
            sx={{ ...tbBtnSx, color: bulkSettingName !== "" ? "primary.dark" : "var(--color-ai, #1e3a5f)" }}
          >
            一括設定
          </Button>
          <Menu
            anchorEl={anchorElMenuBulkSetting}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElMenuBulkSetting)}
            onClose={handleCloseMenuBulkSetting}
          >
            {bulkSettingNames.map((name, i) => (
              <MenuItem
                key={name}
                data-testid={bulkSettingTestIds[i]}
                onClick={() => {
                  if (setBulkSettingName) setBulkSettingName(name);
                  handleCloseMenuBulkSetting();
                }}
              >
                <Box className="header-manu">{name}</Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {/* エクスポート */}
      {getXLSXDataNames.length > 0 && !isSmartPhone ? (
        <>
          <Button
            variant="outlined"
            size="small"
            disabled={totalCount !== undefined && totalCount === 0}
            startIcon={<FileDownloadIcon sx={{ fontSize: "16px !important" }} />}
            onClick={(e) => {
              if (getXLSXDataNames.length === 1) {
                setGetXLSXDataName(getXLSXDataNames[0]);
              } else {
                handleOpenMenuGetXLSXData(e);
              }
            }}
            sx={{ ...tbBtnSx, color: getXLSXDataName !== "" ? "primary.dark" : "var(--color-ai, #1e3a5f)" }}
          >
            エクスポート
          </Button>
          <Menu
            anchorEl={anchorElMenuGetXLSXData}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElMenuGetXLSXData)}
            onClose={handleCloseMenuGetXLSXData}
          >
            {getXLSXDataNames.map((dataName) => (
              <MenuItem
                key={dataName}
                onClick={() => {
                  setGetXLSXDataName(dataName);
                  handleCloseMenuGetXLSXData();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {/* CSV取込 */}
      {addCSVDataNames.length > 0 && !isSmartPhone ? (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon sx={{ fontSize: "16px !important" }} />}
            onClick={(e) => {
              if (addCSVDataNames.length === 1) {
                setAddCSVDataName(addCSVDataNames[0]);
              } else {
                handleOpenMenuAddCSVData(e);
              }
            }}
            sx={{ ...tbBtnSx, color: addCSVDataName !== "" ? "primary.dark" : "var(--color-ai, #1e3a5f)" }}
          >
            CSV取込
          </Button>
          <Menu
            anchorEl={anchorElMenuAddCSVData}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorElMenuAddCSVData)}
            onClose={handleCloseMenuAddCSVData}
          >
            {addCSVDataNames.map((dataName) => (
              <MenuItem
                key={dataName}
                onClick={() => {
                  setAddCSVDataName(dataName);
                  handleCloseMenuAddCSVData();
                }}
              >
                <Box className="header-manu">{dataName}</Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {/* 表示項目の設定 */}
      {pathname === "/toss_up" ||
      pathname === "/contract" ||
      pathname === "/runking" ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon sx={{ fontSize: "16px !important" }} />}
          onClick={openColumnSettingWindow}
          sx={{ ...tbBtnSx, color: columnSettingOpen ? "primary.dark" : "var(--color-ai, #1e3a5f)" }}
        >
          表示項目の設定
        </Button>
      ) : null}

      {/* 検索 */}
      {serch ? (
        <Button
          data-testid="toolbar-search-btn"
          variant="outlined"
          size="small"
          startIcon={<SearchIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => setSerchOpen(!serchOpen)}
          sx={tbBtnSx}
        >
          検索
        </Button>
      ) : null}
    </Box>
    {/* cmd-po_1230 rw1: fixed化で文書フローから外れるツールバーの実高ぶんの隙間（content-jump 防止）。SP限定=PCはDOMごと非描画でdiff0 */}
    {isSmartPhone && (
      <Box
        aria-hidden
        data-testid="table-toolbar-spacer"
        sx={{ height: toolbarHeight, flexShrink: 0 }}
      />
    )}
    </>
  );
}

export default TableTopBar;
